// Stampa diretta su stampanti termiche Bluetooth (BLE) tramite Web Bluetooth API.
// Funziona su Chrome/Android (e Chrome desktop) con app servita in HTTPS.
// Invia comandi ESC/POS al caratteristico di scrittura della stampante.
//
// NOTA: il browser supporta solo Bluetooth Low Energy (BLE), non il Bluetooth
// "classico" (SPP). Funziona con le stampanti termiche che espongono un servizio
// GATT scrivibile (gran parte delle stampantine 58/80mm BLE; la Epson TM-P80II
// supporta la modalità BLE).

import { receiptLabels } from './receiptLabels';

const ESC = 0x1b, GS = 0x1d, LF = 0x0a;

// Servizi GATT noti delle stampanti termiche (per il filtro requestDevice).
const KNOWN_SERVICES = [
  0x18f0, // generico ESC/POS (write char 0x2af1)
  0xff00, // generico
  0xffe0, // moduli HM-10
  0xfee7, // Tencent/alcune cinesi
  0x49535343, // (non valido come 16-bit, gestito sotto come UUID完整)
  '000018f0-0000-1000-8000-00805f9b34fb',
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip transparent UART
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // alcune stampanti
];

// Stato di connessione (in memoria, per sessione).
let _device = null;
let _characteristic = null;

export function isWebBluetoothSupported() {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth;
}

export function btConnectedName() {
  return _device && _device.gatt && _device.gatt.connected ? (_device.name || 'Bluetooth') : null;
}

/** Trova il primo caratteristico scrivibile tra tutti i servizi del device. */
async function findWritableCharacteristic(server) {
  const services = await server.getPrimaryServices();
  for (const svc of services) {
    let chars = [];
    try { chars = await svc.getCharacteristics(); } catch (e) { continue; }
    for (const ch of chars) {
      const p = ch.properties || {};
      if (p.write || p.writeWithoutResponse) return ch;
    }
  }
  throw new Error('Nessun servizio di stampa trovato su questa stampante Bluetooth.');
}

/** Apre la finestra di selezione Bluetooth e connette la stampante. */
export async function connectBtPrinter() {
  if (!isWebBluetoothSupported()) {
    throw new Error('Il Bluetooth dal browser non è supportato su questo dispositivo. Usa Chrome su Android.');
  }
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: KNOWN_SERVICES.filter((s) => typeof s === 'string' || (Number.isInteger(s) && s <= 0xffff)),
  });
  device.addEventListener('gattserverdisconnected', () => { _characteristic = null; });
  const server = await device.gatt.connect();
  const ch = await findWritableCharacteristic(server);
  _device = device;
  _characteristic = ch;
  return device.name || 'Bluetooth';
}

/** Riconnette al device già selezionato, se necessario. */
async function ensureConnected() {
  if (_characteristic && _device && _device.gatt && _device.gatt.connected) return _characteristic;
  if (_device && _device.gatt) {
    const server = await _device.gatt.connect();
    _characteristic = await findWritableCharacteristic(server);
    return _characteristic;
  }
  throw new Error('Stampante Bluetooth non connessa. Premi "Connetti stampante".');
}

/** Scrive i byte in piccoli blocchi (limite MTU del BLE). */
async function writeBytes(ch, bytes) {
  const CHUNK = 180;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    if (ch.properties && ch.properties.writeWithoutResponse) {
      // eslint-disable-next-line no-await-in-loop
      await ch.writeValueWithoutResponse(slice);
    } else {
      // eslint-disable-next-line no-await-in-loop
      await ch.writeValue(slice);
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 20));
  }
}

// ---------- Encoder ESC/POS ----------
// Mappa caratteri accentati / € verso la code page PC858.
const CP858 = {
  '\u20ac': 0xd5, // €
  'Ç': 0x80, 'ü': 0x81, 'é': 0x82, 'â': 0x83, 'ä': 0x84, 'à': 0x85, 'å': 0x86, 'ç': 0x87,
  'ê': 0x88, 'ë': 0x89, 'è': 0x8a, 'ï': 0x8b, 'î': 0x8c, 'ì': 0x8d, 'Ä': 0x8e, 'Å': 0x8f,
  'É': 0x90, 'æ': 0x91, 'Æ': 0x92, 'ô': 0x93, 'ö': 0x94, 'ò': 0x95, 'û': 0x96, 'ù': 0x97,
  'ÿ': 0x98, 'Ö': 0x99, 'Ü': 0x9a, 'ø': 0x9b, '£': 0x9c, 'Ø': 0x9d, '×': 0x9e,
  'á': 0xa0, 'í': 0xa1, 'ó': 0xa2, 'ú': 0xa3, 'ñ': 0xa4, 'Ñ': 0xa5, 'ª': 0xa6, 'º': 0xa7,
  '¿': 0xa8, 'õ': 0xe4, 'Õ': 0xe5, 'ã': 0xc6, 'Ã': 0xc7,
};

function encodeText(str) {
  const out = [];
  for (const c of String(str)) {
    const code = c.charCodeAt(0);
    if (code < 0x80) out.push(code);
    else if (CP858[c] !== undefined) out.push(CP858[c]);
    else out.push(0x3f); // '?'
  }
  return out;
}

const colsFor = (paperWidth) => (Number(paperWidth) === 58 ? 32 : 48);
const eur = (n) => `\u20ac ${Number(n || 0).toFixed(2)}`;
const localeMap = { it: 'it-IT', es: 'es-ES', en: 'en-GB', fr: 'fr-FR', pt: 'pt-PT' };
const fmtDate = (iso, lang) => {
  if (iso === undefined || iso === null || iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso); // già formattata dal backend
  try { return d.toLocaleString(localeMap[(lang || 'it').toLowerCase()] || 'it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(iso); }
};
// Lunghezza minima scontrino (~50mm) anche con un solo prodotto.
const MM_PER_LINE = 4;
const MIN_RECEIPT_MM = 50;
function leftRight(left, right, cols) {
  left = String(left || ''); right = String(right || '');
  const space = cols - left.length - right.length;
  if (space >= 1) return left + ' '.repeat(space) + right;
  return left.slice(0, Math.max(0, cols - right.length - 1)) + ' ' + right;
}

// Costruttore di flusso ESC/POS.
function makeBuilder() {
  const buf = [];
  const push = (...b) => b.forEach((x) => buf.push(x));
  const api = {
    init() { push(ESC, 0x40); push(ESC, 0x74, 19); return api; }, // reset + code page PC858
    align(n) { push(ESC, 0x61, n); return api; }, // 0 left,1 center,2 right
    bold(on) { push(ESC, 0x45, on ? 1 : 0); return api; },
    size(big) { push(GS, 0x21, big ? 0x11 : 0x00); return api; }, // doppia altezza+larghezza
    // Scala esplicita: w/h da 1 a 8 (GS ! n -> nibble alto larghezza, basso altezza).
    sizeWH(w = 1, h = 1) {
      const ww = Math.max(1, Math.min(8, w)) - 1;
      const hh = Math.max(1, Math.min(8, h)) - 1;
      push(GS, 0x21, (ww << 4) | hh); return api;
    },
    text(s) { encodeText(s).forEach((b) => buf.push(b)); return api; },
    line(s = '') { api.text(s); buf.push(LF); return api; },
    feed(n = 1) { for (let i = 0; i < n; i += 1) buf.push(LF); return api; },
    lineCount() { return buf.reduce((n, x) => n + (x === LF ? 1 : 0), 0); },
    cut() { push(GS, 0x56, 0x42, 0x00); return api; }, // feed + taglio parziale
    bytes() { return Uint8Array.from(buf); },
  };
  return api;
}

/** Chiude lo scontrino garantendo una lunghezza minima (~50mm) prima del taglio. */
function finishWithMinLength(b, clearance = 3) {
  const estMm = b.lineCount() * MM_PER_LINE;
  const pad = Math.max(clearance, Math.ceil((MIN_RECEIPT_MM - estMm) / MM_PER_LINE));
  b.feed(pad).cut();
  return b.bytes();
}

/** Costruisce i byte ESC/POS di una ricevuta. */
export function buildReceiptBytes(data, paperWidth = 80) {
  const cols = colsFor(paperWidth);
  const r = data.restaurant || {};
  const t = data.template || {};
  const o = data.order || {};
  const L = data.labels || receiptLabels(data.lang);
  const sep = '-'.repeat(cols);
  const b = makeBuilder().init();

  b.align(1);
  if (r.name) { b.bold(true).size(true).line(r.name).size(false).bold(false); }
  if ((t.show_address === undefined || t.show_address) && r.address) b.line(r.address);
  if ((t.show_address === undefined || t.show_address) && r.address_line2) b.line(r.address_line2);
  if ((t.show_phone === undefined || t.show_phone) && r.phone) b.line(`${L.phone_label || 'Tel'}: ${r.phone}`);
  if ((t.show_vat === undefined || t.show_vat) && r.vat) b.line(`${L.vat_label || 'P.IVA'} ${r.vat}`);
  (t.header_lines || []).filter(Boolean).forEach((l) => b.line(l));
  b.align(0).line(sep);
  if (data.title) { b.align(1).bold(true).line(String(data.title).toUpperCase()).bold(false); }
  if (data.is_fiscal === false) b.align(1).line(L.non_fiscal || 'DOCUMENTO NON FISCALE');
  b.align(0);
  const covers = o.covers ? `${L.covers || 'Coperti'}: ${o.covers}` : '';
  if (o.table_name || covers) b.line(leftRight(o.table_name || '', covers, cols));
  b.line(leftRight(fmtDate(data.datetime_iso || data.datetime, data.lang), o.waiter || '', cols));
  b.line(sep);
  (data.lines || []).forEach((it) => {
    b.line(leftRight(`${it.qty}x ${it.name}${it.variant ? ` (${it.variant})` : ''}`, eur(it.line_total), cols));
    (it.extras || []).forEach((e) => b.line(`  + ${e.name}${e.price ? `  ${eur(e.price)}` : ''}`));
    if (it.notes) b.line(`  > ${it.notes}`);
  });
  b.line(sep);
  b.line(leftRight(L.subtotal || 'Subtotale', eur(data.items_total), cols));
  if (Number(data.discount || 0) > 0) b.line(leftRight(`${L.discount || 'Sconto'}${(data.discount_label || data.discount_code) ? ` (${data.discount_label || data.discount_code})` : ''}`, `- ${eur(data.discount)}`, cols));
  b.bold(true).size(true).line(leftRight(L.total || 'TOTALE', eur(data.total), cols)).size(false).bold(false);
  b.line(sep);
  b.line(leftRight(L.taxable || 'Imponibile', eur(data.imponibile), cols));
  const vatTxt = String(L.vat_included || 'IVA {rate}% inclusa').replace('{rate}', Number(data.vat_rate || 0).toFixed(0));
  b.line(leftRight(vatTxt, eur(data.iva), cols));
  b.align(1);
  (t.footer_lines || []).filter(Boolean).forEach((l) => b.line(l));
  return finishWithMinLength(b);
}

/** Costruisce i byte ESC/POS di una COMANDA (cucina/bar/cameriere) via Bluetooth.
 * Testo ingrandito (~+50%), spazio tra postazione e tavolo e tra i prodotti,
 * lunghezza minima 50mm. */
export function buildComandaBytes(data, paperWidth = 80) {
  const cols = colsFor(paperWidth);
  const L = data.labels || receiptLabels(data.lang);
  const o = data.order || {};
  const sep = '-'.repeat(cols);
  let stationLabel;
  if (data.station === 'bar') stationLabel = (L.bar || 'BAR');
  else if (data.station === 'cameriere') stationLabel = (L.waiter || 'CAMERIERE');
  else stationLabel = (L.kitchen || 'CUCINA');
  stationLabel = String(stationLabel).toUpperCase();
  const b = makeBuilder().init();
  b.align(1).bold(true).sizeWH(1, 3).line(`** ${stationLabel} **`).sizeWH(1, 1).bold(false);
  b.feed(1); // spazio tra postazione e tavolo
  if (o.table_name) b.bold(true).sizeWH(1, 3).line(o.table_name).sizeWH(1, 1).bold(false);
  const dt = fmtDate(data.datetime_iso || data.datetime, data.lang);
  if (dt) b.sizeWH(1, 2).line(dt).sizeWH(1, 1);
  if (o.waiter) b.sizeWH(1, 2).line(`${L.waiter || 'Cameriere'}: ${o.waiter}`).sizeWH(1, 1);
  b.align(0).line(sep);
  (data.lines || []).forEach((it) => {
    b.bold(true).sizeWH(1, 3).line(`${it.qty}x ${it.name}${it.variant ? ` (${it.variant})` : ''}`).sizeWH(1, 1).bold(false);
    (it.extras || []).forEach((e) => b.sizeWH(1, 2).line(`   + ${e.name}`).sizeWH(1, 1));
    if (it.notes) b.sizeWH(1, 2).line(`   >> ${it.notes}`).sizeWH(1, 1);
    b.feed(1); // spazio tra un prodotto e l'altro
  });
  return finishWithMinLength(b);
}

/** Byte di una stampa di prova. */
export function buildTestBytes(name = 'Restaurant Manager Pro', paperWidth = 80) {
  const cols = colsFor(paperWidth);
  const b = makeBuilder().init();
  b.align(1).bold(true).size(true).line(name).size(false).bold(false)
    .line('-'.repeat(cols))
    .bold(true).line('STAMPA DI PROVA').bold(false)
    .line('Bluetooth')
    .line(new Date().toLocaleString('it-IT'))
    .line('-'.repeat(cols))
    .line('Stampante collegata!')
    .feed(3).cut();
  return b.bytes();
}

/** Stampa una ricevuta via Bluetooth. */
export async function btPrintReceipt(data, paperWidth = 80) {
  const ch = await ensureConnected();
  await writeBytes(ch, buildReceiptBytes(data, paperWidth));
  return true;
}

/** Stampa una comanda via Bluetooth (richiede stampante BT già connessa). */
export async function btPrintComanda(data, paperWidth = 80) {
  const ch = await ensureConnected();
  await writeBytes(ch, buildComandaBytes(data, paperWidth));
  return true;
}

/** Stampa di prova via Bluetooth. */
export async function btTestPrint(name, paperWidth = 80) {
  const ch = await ensureConnected();
  await writeBytes(ch, buildTestBytes(name, paperWidth));
  return true;
}
